import { useContext } from 'react'
import { Outlet, useLocation, Navigate } from 'react-router-dom'
import { UserProvider, useUser } from '@/contexts/UserContext'
import { PermissionProvider } from '@/contexts/PermissionProvider'
import { PermissionContext, usePermissionLoading } from '@/hooks/usePermission'
import { evaluatePathAccess, canAccessPath } from '@/lib/routeAccess'
import { AppError } from '@/lib/appError'
import AccessDenied from '@/components/AccessDenied'
import NotFound from '@/components/NotFound'
import AppLoading from '@/components/AppLoading'

/**
 * RouteEnforcerInner — enforcement logic for all access control.
 *
 * Used as a pathless layout route in App.tsx, wrapping every route in the app.
 *
 * Pre-checks (not route-access decisions):
 *   - Loading state
 *   - mustChangePassword — meta-auth; redirect before route evaluation
 *
 * All route access decisions go through evaluatePathAccess(), which returns:
 *   undefined         — path not in registry; renders NotFound
 *   'ok'              — render the route
 *   'unauthenticated' — redirect to /login
 *   'denied'          — render AccessDenied inline
 *   'unassigned'      — redirect to /profile (no active deployment)
 */
function RouteEnforcerInner() {
  const location = useLocation()
  const { user, employee, incidentUnit, loading, mustChangePassword } = useUser()
  const permLoading = usePermissionLoading()
  const { permissions } = useContext(PermissionContext)

  // Display loading while user and permissions are loading.
  if (loading || permLoading) {
    return <AppLoading />
  }

  // User without employee is an error. no user means no auth yet, redirect to /login later.
  if (user && !employee) {
    throw new AppError('Your employee profile could not be loaded. Check your connection or contact your administrator.', ['signout'])
  }

  // Forced password change in user data.
  if (mustChangePassword && location.pathname !== '/change-password') {
    return <Navigate to="/change-password" replace />
  }

  // Evaluate route via routeAccess and redirect according to return.
  const accessUser = user ? { incidentUnit: incidentUnit ?? null, permissions } : null
  const result = evaluatePathAccess(location.pathname, accessUser)
  switch (result) {
    case undefined:         return <NotFound />
    case 'unauthenticated': return <Navigate to="/login" replace state={{ from: location }} />
    case 'denied':          return <AccessDenied />
    case 'unassigned':      return <Navigate to="/profile" replace state={{ unassigned: true }} />
    case 'ok':              return <Outlet />
    default: {
      const _: never = result
      return <Navigate to="/login" replace />
    }
  }
}

/** Provider wrapper so RouteEnforcerInner can consume UserContext and PermissionContext via hooks. */
export default function RouteEnforcer() {
  return (
    <UserProvider>
      <PermissionProvider>
        <RouteEnforcerInner />
      </PermissionProvider>
    </UserProvider>
  )
}

/**
 * Hook — asks RouteEnforcer whether the current user can reach a path.
 * Assembles AccessUser from context and delegates to canAccessPath.
 * Safe to call from any component inside RouteEnforcer's provider tree.
 */
export function useCanAccessPath(path: string): boolean {
  const { incidentUnit } = useUser()
  const { permissions } = useContext(PermissionContext)
  return canAccessPath(path, { incidentUnit: incidentUnit ?? null, permissions })
}
